import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Download, CheckCircle, Loader2, Check, Pencil, X } from 'lucide-react';
import { useState } from 'react';
import { clsx } from 'clsx';
import { api } from '../lib/api';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useToast } from '../components/ui/Toast';
import { usePermissions } from '../hooks/usePermissions';

const DOMAIN_LABELS: Record<string, string> = {
  household: 'Household',
  income: 'Income',
  tax: 'Tax',
  insurance: 'Insurance',
  estate: 'Estate',
  account: 'Account',
};

const DOMAIN_ORDER = ['household', 'income', 'tax', 'insurance', 'estate', 'account'];

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-red-100 text-red-700',
};

function confidenceLevel(c: number) {
  if (c >= 0.9) return 'high';
  if (c >= 0.7) return 'medium';
  return 'low';
}

function formatFactValue(_factType: string, value: any): string {
  if (value?.amount != null) return `$${Number(value.amount).toLocaleString()}`;
  if (value?.status) return value.status;
  if (value?.first && value?.last) return `${value.first} ${value.last}`;
  if (value?.name) return value.name;
  if (typeof value === 'string') return value;
  return JSON.stringify(value);
}

function humanizeFactType(factType: string): string {
  return factType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = usePermissions();
  const canWrite = can('snapshots:write');

  const { data, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => api.getDocument(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.document?.processingStatus;
      return status === 'uploaded' || status === 'processing' ? 3000 : false;
    },
  });

  const verifyMutation = useMutation({
    mutationFn: ({ factId, action, correctedValue }: { factId: string; action: string; correctedValue?: unknown }) =>
      api.verifyFact(factId, { action, correctedValue }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] });
      queryClient.invalidateQueries({ queryKey: ['profileCompleteness'] });
      queryClient.invalidateQueries({ queryKey: ['nextActions'] });
    },
    onError: () => toast.error('Failed to verify fact'),
  });

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const { document: doc, facts, factCounts } = data;
  const resolvedCount = factCounts.confirmed + factCounts.corrected + factCounts.rejected;

  // Group facts by domain
  const grouped = DOMAIN_ORDER.map((domain) => ({
    domain,
    label: DOMAIN_LABELS[domain] ?? domain,
    facts: (facts ?? []).filter((f: any) => f.domain === domain),
  })).filter((g) => g.facts.length > 0);

  const handleConfirmAll = () => {
    if (!canWrite) return;
    const pendingHighConfidence = (facts ?? []).filter(
      (f: any) => f.verificationStatus === 'pending' && f.confidence >= 0.9
    );
    pendingHighConfidence.forEach((f: any) => {
      verifyMutation.mutate({ factId: f.id, action: 'confirm' });
    });
    if (pendingHighConfidence.length > 0) {
      toast.success(`Confirmed ${pendingHighConfidence.length} high-confidence facts`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/documents" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{doc.title}</h1>
            <p className="text-sm text-gray-500">
              {doc.documentType === 'tax_return' ? 'Tax Return' : doc.documentType} &middot; Tax Year {doc.taxYear ?? 'Unknown'}
            </p>
          </div>
        </div>
        {doc.downloadUrl && (
          <a
            href={doc.downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            <Download className="h-4 w-4" /> Download
          </a>
        )}
      </div>

      {/* Status bar */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            {doc.processingStatus === 'failed' ? 'Extraction Failed' :
             doc.processingStatus === 'processing' ? 'Processing...' :
             `${resolvedCount} of ${factCounts.total} facts reviewed`}
          </span>
          {doc.extractionConfidence != null && (
            <span className="text-xs text-gray-500">
              Avg confidence: {Math.round(doc.extractionConfidence * 100)}%
            </span>
          )}
        </div>
        {factCounts.total > 0 && (
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all"
              style={{ width: `${(resolvedCount / factCounts.total) * 100}%` }}
            />
          </div>
        )}
        {doc.processingStatus === 'failed' && doc.extractionError && (
          <p className="mt-2 text-sm text-red-600">{doc.extractionError}</p>
        )}
      </div>

      {/* Confirm All button */}
      {factCounts.pending > 0 && (
        <div className="flex justify-end">
          <button
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 bg-green-50 rounded-md hover:bg-green-100"
            onClick={handleConfirmAll}
          >
            <CheckCircle className="h-4 w-4" />
            Confirm All High-Confidence
          </button>
        </div>
      )}

      {/* Facts grouped by domain */}
      {doc.processingStatus === 'processing' ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
          <span className="ml-3 text-gray-600">Extracting financial data...</span>
        </div>
      ) : grouped.length === 0 && doc.processingStatus !== 'failed' ? (
        <p className="text-center text-gray-500 py-8">No facts extracted yet.</p>
      ) : (
        grouped.map((group) => (
          <div key={group.domain} className="bg-white rounded-lg border">
            <div className="px-4 py-3 border-b bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700">
                {group.label}
                <span className="ml-2 font-normal text-gray-500">
                  ({group.facts.filter((f: any) => f.verificationStatus !== 'pending').length}/{group.facts.length} reviewed)
                </span>
              </h3>
            </div>
            <div className="divide-y divide-gray-100">
              {group.facts.map((fact: any) => (
                <FactRow
                  key={fact.id}
                  fact={fact}
                  onConfirm={() => verifyMutation.mutate({ factId: fact.id, action: 'confirm' })}
                  onCorrect={(val) => verifyMutation.mutate({ factId: fact.id, action: 'correct', correctedValue: val })}
                  onReject={() => verifyMutation.mutate({ factId: fact.id, action: 'reject' })}
                  isPending={verifyMutation.isPending}
                />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function FactRow({
  fact,
  onConfirm,
  onCorrect,
  onReject,
  isPending,
}: {
  fact: any;
  onConfirm: () => void;
  onCorrect: (val: any) => void;
  onReject: () => void;
  isPending: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const level = confidenceLevel(fact.confidence);
  const isResolved = fact.verificationStatus !== 'pending';

  return (
    <div className="px-4 py-3 flex items-center gap-3">
      {/* Fact info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900">
            {humanizeFactType(fact.factType)}
          </span>
          <span className={clsx('px-1.5 py-0.5 rounded text-xs', CONFIDENCE_COLORS[level])}>
            {Math.round(fact.confidence * 100)}%
          </span>
        </div>
        <div className="text-sm text-gray-700 mt-0.5">
          {editing ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="border rounded px-2 py-1 text-sm w-full max-w-xs"
              autoFocus
            />
          ) : (
            formatFactValue(fact.factType, fact.correctedValue ?? fact.factValue)
          )}
        </div>
        {fact.pageReference && (
          <span className="text-xs text-gray-400">{fact.pageReference}</span>
        )}
      </div>

      {/* Status / Actions */}
      {isResolved ? (
        <span className={clsx('text-xs px-2 py-0.5 rounded-full', {
          'bg-green-100 text-green-700': fact.verificationStatus === 'confirmed',
          'bg-blue-100 text-blue-700': fact.verificationStatus === 'corrected',
          'bg-red-100 text-red-700': fact.verificationStatus === 'rejected',
        })}>
          {fact.verificationStatus}
        </span>
      ) : (
        <div className="flex items-center gap-1">
          {editing ? (
            <>
              <button
                className="p-1 rounded text-green-600 hover:bg-green-50"
                onClick={() => {
                  const val = fact.factValue?.amount != null
                    ? { ...fact.factValue, amount: Number(editValue) }
                    : { ...fact.factValue, name: editValue };
                  onCorrect(val);
                  setEditing(false);
                }}
                disabled={isPending}
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                className="p-1 rounded text-gray-400 hover:bg-gray-100"
                onClick={() => setEditing(false)}
              >
                <X className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <button
                className="p-1 rounded text-green-600 hover:bg-green-50"
                title="Confirm"
                onClick={onConfirm}
                disabled={isPending}
              >
                <Check className="h-4 w-4" />
              </button>
              <button
                className="p-1 rounded text-blue-600 hover:bg-blue-50"
                title="Correct"
                onClick={() => {
                  setEditValue(
                    fact.factValue?.amount != null
                      ? String(fact.factValue.amount)
                      : fact.factValue?.name ?? JSON.stringify(fact.factValue)
                  );
                  setEditing(true);
                }}
                disabled={isPending}
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                className="p-1 rounded text-red-600 hover:bg-red-50"
                title="Reject"
                onClick={onReject}
                disabled={isPending}
              >
                <X className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
